import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Role, UserRoleVM } from 'app/_models';
import { ToolsService } from 'app/_services/tools.service';

@Component({
    selector: 'app-edit-user-dialog',
    templateUrl: './edit-user-dialog.component.html',
    styleUrls: ['./edit-user-dialog.component.scss'],
})
export class EditUserDialogComponent implements OnInit {
    title = 'User Editor';
    user: UserRoleVM;
    name: string;
    roles: Role[];
    sapidList: string[];
    userEditorForm: FormGroup;
    creating: boolean;

    // Getters for form validation error messages
    get Sapid(): AbstractControl {
        return this.userEditorForm.controls.Sapid;
    }
    get Role(): AbstractControl {
        return this.userEditorForm.controls.Role;
    }

    constructor(
        private dialogRef: MatDialogRef<EditUserDialogComponent>,
        private formBuilder: FormBuilder,
        private toolsService: ToolsService,
        @Inject(MAT_DIALOG_DATA)
        public data: { creating: boolean; userRole: UserRoleVM; roles: Role[]; sapidList: string[]; title: string }
    ) {
        this.title = data.title;
        this.creating = data.creating;
        this.user = data.userRole;
        this.roles = data.roles;
        this.sapidList = data.sapidList;

        if (this.creating) {
            this.name = '';
        } else {
            this.name = this.user.firstName + ' ' + this.user.lastName;
        }
    }

    ngOnInit(): void {
        this.userEditorForm = this.formBuilder.group({
            Name: [{ value: this.name, disabled: true }],
            Sapid: [this.user.sapid, [Validators.required, Validators.maxLength(8), isSAPIDTaken(this.user.sapid, this.sapidList)]],
            RoleId: [this.user.roleId],
        });
    }

    formatSAPID() {
        const sapid = this.Sapid.value;
        if (sapid && sapid.length > 0 ) {
            this.Sapid.setValue(this.padLeft(sapid, "0", 8));
        }
    }
    
    padLeft(text: string, padChar: string, size: number): string {
        return (String(padChar).repeat(size) + text).substr(size * -1, size);
    }

    onNoClick(): void {
        this.dialogRef.close(null);
    }

    onSubmit(formValues: any): void {
        this.formatSAPID();
        this.userEditorForm.markAllAsTouched();

        if (this.userEditorForm.valid) {
            const today = this.toolsService.toISOLocal(new Date());

            this.user.sapid = formValues.Sapid;
            this.user.roleId = formValues.RoleId;
            this.user.roleName = this.roles.find((role) => role.roleId === formValues.RoleId).roleName;

            this.dialogRef.close(this.user);
            this.userEditorForm.reset();
        }
    }
}

/* Validation function to see if the inputted SAPID already exists. If so, the field will become invalid.
 * This error will NOT occurr if, while editing an existing SAPID, the user reverts changes to match the
 * original name provide to the form prior to saving.
 */
function isSAPIDTaken(sapid: string, sapidList: string[]): ValidatorFn {
    return (control: FormControl): ValidationErrors | null => {
        const isSAPIDTaken = sapidList.some((value) => value === control.value);
        const error = isSAPIDTaken && control.dirty && control.value !== sapid ? { isSapidTaken: true } : null;
        return error;
    };
}
